import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiUsers, FiUserPlus, FiAward, FiBriefcase, FiLinkedin, FiArrowUpRight, FiLoader, FiMail } from 'react-icons/fi';

/**
 * CapabilityDisplay - Membership-driven introduction capabilities
 *
 * Cards follow a progressive funnel:
 * 1. Share LinkedIn Profile (if missing)
 * 2. Upgrade membership (if tier too low)
 * 3. Request Introduction (if fully eligible)
 *
 * Visibility rules:
 * - Peer Networking & Seek Mentorship: Always visible, always clickable
 * - Offer Mentorship: Hidden unless can_offer_mentorship (score >= 6.0)
 * - Board Pathway: Hidden unless Fellow + score >= 7.0 (special CTA for qualified non-Fellows)
 */
const CapabilityDisplay = ({ eligibility, onRequestIntroduction, onShareLinkedIn }) => {
  if (!eligibility) {
    return null;
  }

  const capabilities = eligibility.capabilities || {};
  const isEligible = eligibility.matching_eligible;
  const consentGiven = eligibility.consent_given;
  const hasLinkedIn = eligibility.has_linkedin_profile;
  const hasScore = eligibility.has_score;
  const tier = eligibility.capability_tier || 'emerging';
  const membershipTier = eligibility.membership_tier || {};
  const tierLevel = membershipTier.tier_level ?? 0;
  const upgradeInfo = eligibility.upgrade_info || {};
  const tiersEnabled = !!eligibility.membership_tier; // tiers system is active

  // Determine peer networking request type based on capability tier
  const getPeerRequestType = () => {
    if (tier === 'executive') return 'PEER_SENIOR';
    if (tier === 'professional') return 'PEER_MID_LEVEL';
    return 'PEER_MID_LEVEL'; // Default for funnel display
  };

  const formatPrice = (priceCents) => {
    if (!priceCents) return null;
    return `$${(priceCents / 100).toLocaleString()}`;
  };

  // Get upgrade CTA text for a feature
  const getUpgradeInfo = (featureKey) => {
    const info = upgradeInfo[featureKey];
    if (!info) return null;
    const price = formatPrice(info.price_cents);
    return {
      tierName: info.required_tier_name,
      tierSlug: info.required_tier_slug,
      requiredLevel: info.required_tier_level,
      price: price ? `${price}/year` : null,
      paymentUrl: info.hubspot_payment_url,
    };
  };

  // ========================================
  // PEER NETWORKING CARD
  // ========================================
  const renderPeerNetworking = () => {
    const peerUpgrade = getUpgradeInfo('peer_networking');
    const requestType = getPeerRequestType();

    // State 1: No LinkedIn
    if (!hasLinkedIn) {
      return (
        <FunnelCard
          icon={FiLinkedin}
          title="Peer Networking"
          description="Connect with professionals at your career level"
          borderColor="border-amber-300 dark:border-amber-600"
          iconBg="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
          cta={
            <button
              onClick={onShareLinkedIn}
              className="mt-3 w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors text-sm"
            >
              Import Your Profile →
            </button>
          }
          subtext="Import your profile to get started"
        />
      );
    }

    // State 2: Has LinkedIn, needs tier upgrade (show regardless of scoring status)
    if (tiersEnabled && peerUpgrade && tierLevel < peerUpgrade.requiredLevel) {
      const ctaText = peerUpgrade.price
        ? `Upgrade to ${peerUpgrade.tierName} — ${peerUpgrade.price}`
        : `Contact us to upgrade`;

      return (
        <FunnelCard
          icon={FiUsers}
          title="Peer Networking"
          description="Connect with professionals at your career level. Upgrade to get started."
          borderColor="border-indigo-300 dark:border-indigo-600"
          iconBg="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400"
          cta={
            peerUpgrade.paymentUrl ? (
              <a
                href={peerUpgrade.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                {ctaText} <FiArrowUpRight className="w-4 h-4" />
              </a>
            ) : (
              <a
                href="mailto:info@tla.engineer?subject=Membership%20Upgrade%20Inquiry"
                className="mt-3 w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                Contact us to upgrade <FiMail className="w-4 h-4" />
              </a>
            )
          }
          subtext={`${peerUpgrade.tierName} membership required for Peer Networking`}
        />
      );
    }

    // State 3c: Has tier, scoring in progress
    if (hasLinkedIn && !hasScore) {
      return (
        <FunnelCard
          icon={FiUsers}
          title="Peer Networking"
          description="Connect with professionals at your career level"
          borderColor="border-blue-200 dark:border-blue-700"
          iconBg="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          cta={
            <div className="mt-3 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <FiLoader className="w-4 h-4 animate-spin" />
              <span>Setting up your matches — we'll notify you when ready</span>
            </div>
          }
        />
      );
    }

    // State 3b: Has tier + scored, no consent
    if (isEligible && !consentGiven) {
      return (
        <FunnelCard
          icon={FiUsers}
          title="Peer Networking"
          description="Connect with professionals at your career level"
          borderColor="border-blue-200 dark:border-blue-700"
          iconBg="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          cta={
            <p className="mt-3 text-sm text-blue-600 dark:text-blue-400">
              Accept the matching terms above to get started
            </p>
          }
        />
      );
    }

    // State 3: Fully eligible — green card
    if (capabilities.can_request_peer_networking && isEligible && consentGiven) {
      return (
        <EligibleCard
          icon={FiUsers}
          title="Peer Networking"
          description="Connect with alumni at similar career stages for mutual support and networking."
          requestType={requestType}
          onRequestIntroduction={onRequestIntroduction}
        />
      );
    }

    // Fallback: show as not yet available
    return (
      <FunnelCard
        icon={FiUsers}
        title="Peer Networking"
        description="Connect with professionals at your career level"
        borderColor="border-gray-200 dark:border-gray-700"
        iconBg="bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
        subtext="Complete your profile to unlock"
      />
    );
  };

  // ========================================
  // SEEK MENTORSHIP CARD
  // ========================================
  const renderSeekMentorship = () => {
    const mentorUpgrade = getUpgradeInfo('seek_mentorship');

    // State 1: No LinkedIn
    if (!hasLinkedIn) {
      return (
        <FunnelCard
          icon={FiLinkedin}
          title="Seek Mentorship"
          description="Request introductions to senior alumni who can guide your career"
          borderColor="border-amber-300 dark:border-amber-600"
          iconBg="bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
          cta={
            <button
              onClick={onShareLinkedIn}
              className="mt-3 w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors text-sm"
            >
              Import Your Profile →
            </button>
          }
          subtext="Import your profile to get started"
        />
      );
    }

    // State 2: Has LinkedIn, needs tier upgrade (show regardless of scoring status)
    if (tiersEnabled && mentorUpgrade && tierLevel < mentorUpgrade.requiredLevel) {
      const currentTierName = membershipTier.tier_name || 'Community';
      const upgradeFromText = tierLevel > 0 ? `Upgrade from ${currentTierName} to ${mentorUpgrade.tierName}` : `Upgrade to ${mentorUpgrade.tierName}`;
      const ctaText = mentorUpgrade.price
        ? `${upgradeFromText} — ${mentorUpgrade.price}`
        : `Contact us to upgrade`;

      return (
        <FunnelCard
          icon={FiUserPlus}
          title="Seek Mentorship"
          description="Request introductions to senior alumni who can guide your career. Upgrade to get started."
          borderColor="border-indigo-300 dark:border-indigo-600"
          iconBg="bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400"
          cta={
            mentorUpgrade.paymentUrl ? (
              <a
                href={mentorUpgrade.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                {ctaText} <FiArrowUpRight className="w-4 h-4" />
              </a>
            ) : (
              <a
                href="mailto:info@tla.engineer?subject=Membership%20Upgrade%20Inquiry"
                className="mt-3 w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                Contact us to upgrade <FiMail className="w-4 h-4" />
              </a>
            )
          }
          subtext={`${mentorUpgrade.tierName} membership required for Seek Mentorship`}
        />
      );
    }

    // State 3c: Scoring in progress
    if (hasLinkedIn && !hasScore) {
      return (
        <FunnelCard
          icon={FiUserPlus}
          title="Seek Mentorship"
          description="Request introductions to senior alumni who can guide your career"
          borderColor="border-blue-200 dark:border-blue-700"
          iconBg="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          cta={
            <div className="mt-3 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <FiLoader className="w-4 h-4 animate-spin" />
              <span>Setting up your matches — we'll notify you when ready</span>
            </div>
          }
        />
      );
    }

    // State 3b: Scored, no consent
    if (isEligible && !consentGiven) {
      return (
        <FunnelCard
          icon={FiUserPlus}
          title="Seek Mentorship"
          description="Request introductions to senior alumni who can guide your career"
          borderColor="border-blue-200 dark:border-blue-700"
          iconBg="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          cta={
            <p className="mt-3 text-sm text-blue-600 dark:text-blue-400">
              Accept the matching terms above to get started
            </p>
          }
        />
      );
    }

    // State 3: Fully eligible
    if (capabilities.can_request_mentorship && isEligible && consentGiven) {
      return (
        <EligibleCard
          icon={FiUserPlus}
          title="Seek Mentorship"
          description="Request introductions to senior alumni who can guide your career journey."
          requestType="MENTORSHIP_UP"
          onRequestIntroduction={onRequestIntroduction}
        />
      );
    }

    return (
      <FunnelCard
        icon={FiUserPlus}
        title="Seek Mentorship"
        description="Request introductions to senior alumni who can guide your career"
        borderColor="border-gray-200 dark:border-gray-700"
        iconBg="bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
        subtext="Complete your profile to unlock"
      />
    );
  };

  // ========================================
  // OFFER MENTORSHIP CARD (hidden unless eligible)
  // ========================================
  const renderOfferMentorship = () => {
    if (!capabilities.can_offer_mentorship) {
      return null;
    }

    if (!consentGiven) {
      return (
        <FunnelCard
          icon={FiAward}
          title="Offer Mentorship"
          description="Share your experience by mentoring emerging professionals in the network."
          borderColor="border-blue-200 dark:border-blue-700"
          iconBg="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          cta={
            <p className="mt-3 text-sm text-blue-600 dark:text-blue-400">
              Accept the matching terms above to get started
            </p>
          }
        />
      );
    }

    return (
      <EligibleCard
        icon={FiAward}
        title="Offer Mentorship"
        description="Share your experience by mentoring emerging professionals in the network."
        requestType="MENTORSHIP_HIGH"
        onRequestIntroduction={onRequestIntroduction}
      />
    );
  };

  // ========================================
  // BOARD PATHWAY CARD (hidden unless Fellow + score >= 7.0)
  // ========================================
  const renderBoardPathway = () => {
    const isFellow = tiersEnabled && tierLevel >= 3;
    const hasBoard = capabilities.can_request_board_pathway;

    // Fully eligible: Fellow + score >= 7.0
    if (isFellow && hasBoard) {
      if (!consentGiven) {
        return (
          <FunnelCard
            icon={FiBriefcase}
            title="Board Pathway"
            description="Explore board and advisory opportunities through curated introductions."
            borderColor="border-blue-200 dark:border-blue-700"
            iconBg="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
            cta={
              <p className="mt-3 text-sm text-blue-600 dark:text-blue-400">
                Accept the matching terms above to get started
              </p>
            }
          />
        );
      }

      return (
        <EligibleCard
          icon={FiBriefcase}
          title="Board Pathway"
          description="Explore board and advisory opportunities through curated introductions."
          requestType="BOARD_PATHWAY"
          onRequestIntroduction={onRequestIntroduction}
        />
      );
    }

    // Special case: Has score >= 7.0 but NOT a Fellow — show "Contact us"
    if (hasBoard && !isFellow) {
      return (
        <FunnelCard
          icon={FiBriefcase}
          title="Board Pathway — TLA Fellow Exclusive"
          description="Board and advisory placement introductions for TLA Fellows."
          borderColor="border-gray-300 dark:border-gray-600"
          iconBg="bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
          cta={
            <a
              href="mailto:info@tla.engineer?subject=TLA%20Fellow%20Inquiry"
              className="mt-3 w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            >
              Contact us to learn about becoming a TLA Fellow <FiMail className="w-4 h-4" />
            </a>
          }
        />
      );
    }

    // Otherwise: hidden
    return null;
  };

  // ========================================
  // FALLBACK: When membership tiers are OFF, use legacy behavior
  // ========================================
  if (!tiersEnabled) {
    return <LegacyCapabilityDisplay eligibility={eligibility} onRequestIntroduction={onRequestIntroduction} />;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Your Introduction Capabilities
      </h3>

      {/* Consent prompt for scored users */}
      {isEligible && !consentGiven && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            Accept the matching terms above to start requesting introductions.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderPeerNetworking()}
        {renderSeekMentorship()}
        {renderOfferMentorship()}
        {renderBoardPathway()}
      </div>
    </div>
  );
};

// ========================================
// REUSABLE CARD COMPONENTS
// ========================================

/** Funnel card — shows a CTA to progress through the funnel */
const FunnelCard = ({ icon: Icon, title, description, borderColor, iconBg, cta, subtext }) => (
  <div className={`relative p-4 rounded-lg border-2 transition-all ${borderColor}`}>
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
        <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">{description}</p>
        {cta}
        {subtext && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{subtext}</p>
        )}
      </div>
    </div>
  </div>
);

/** Eligible card — green, fully available for requesting */
const EligibleCard = ({ icon: Icon, title, description, requestType, onRequestIntroduction }) => (
  <div className="relative p-4 rounded-lg border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700 transition-all">
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
          <FiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
        </div>
        <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">{description}</p>
        {onRequestIntroduction && (
          <button
            onClick={() => onRequestIntroduction(requestType)}
            className="mt-3 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
          >
            Request Introduction →
          </button>
        )}
      </div>
    </div>
  </div>
);

// ========================================
// LEGACY DISPLAY (when tiers toggle is OFF)
// ========================================
const LegacyCapabilityDisplay = ({ eligibility, onRequestIntroduction }) => {
  const capabilities = eligibility.capabilities || {};
  const isEligible = eligibility.matching_eligible;
  const consentGiven = eligibility.consent_given;
  const tier = eligibility.capability_tier || 'emerging';

  const getPeerRequestType = () => {
    if (tier === 'executive') return 'PEER_SENIOR';
    if (tier === 'professional') return 'PEER_MID_LEVEL';
    return null;
  };

  const peerRequestType = getPeerRequestType();

  const capabilityCards = [
    {
      key: 'peer_networking',
      title: 'Peer Networking',
      description: 'Connect with alumni at similar career stages for mutual support and networking.',
      icon: FiUsers,
      available: capabilities.can_request_peer_networking && peerRequestType !== null,
      requestType: peerRequestType || 'PEER_MID_LEVEL',
      lockedMessage: tier === 'emerging' ? 'Available at professional tier and above' : null,
    },
    {
      key: 'seek_mentorship',
      title: 'Seek Mentorship',
      description: 'Request introductions to senior alumni who can guide your career journey.',
      icon: FiUserPlus,
      available: capabilities.can_request_mentorship,
      requestType: 'MENTORSHIP_UP',
    },
    {
      key: 'offer_mentorship',
      title: 'Offer Mentorship',
      description: 'Share your experience by mentoring emerging professionals in the network.',
      icon: FiAward,
      available: capabilities.can_offer_mentorship,
      requestType: 'MENTORSHIP_HIGH',
      lockedMessage: 'Available at professional tier and above',
    },
    {
      key: 'board_pathway',
      title: 'Board Pathway',
      description: 'Explore board and advisory opportunities through curated introductions.',
      icon: FiBriefcase,
      available: capabilities.can_request_board_pathway,
      requestType: 'BOARD_PATHWAY',
      lockedMessage: 'Available at executive tier',
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Your Introduction Capabilities
      </h3>

      {!isEligible && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-amber-800 dark:text-amber-200 text-sm">
            {eligibility.next_step?.toLowerCase().includes('import your profile') ? (
              <><Link to="/alumni/profile-management#linkedin-section" className="text-blue-600 dark:text-blue-400 font-medium underline hover:no-underline">Import your profile</Link> to get started</>
            ) : (
              eligibility.next_step || 'Import your profile to get started.'
            )}
          </p>
        </div>
      )}

      {isEligible && !consentGiven && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            Accept the matching terms above to start requesting introductions.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {capabilityCards.map((cap) => {
          const Icon = cap.icon;
          const isAvailable = cap.available && isEligible && consentGiven;
          const isLocked = !cap.available && isEligible;

          return (
            <div
              key={cap.key}
              className={`
                relative p-4 rounded-lg border-2 transition-all
                ${isAvailable
                  ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700'
                  : isLocked
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                    : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30'
                }
              `}
            >
              <div className="flex items-start gap-3">
                <div className={`
                  p-2 rounded-lg
                  ${isAvailable
                    ? 'bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                  }
                `}>
                  <Icon className="w-5 h-5" />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className={`font-medium ${isAvailable ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                      {cap.title}
                    </h4>
                    {isAvailable && (
                      <FiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${isAvailable ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                    {cap.description}
                  </p>

                  {isLocked && cap.lockedMessage && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 italic">
                      {cap.lockedMessage}
                    </p>
                  )}

                  {isAvailable && onRequestIntroduction && (
                    <button
                      onClick={() => onRequestIntroduction(cap.requestType)}
                      className="mt-3 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                    >
                      Request Introduction →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CapabilityDisplay;
